// Computes a `width` (used by MUITable as the cell `minWidth`) for each column
// from the widest value actually present on the current page, so columns shrink
// for short data (Zone, Status) and grow for long data (Employee Name) instead
// of every report hard-coding a guess.

const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 240;
const BODY_CHAR_WIDTH = 7.5;   // ~13px body font
const HEADER_CHAR_WIDTH = 7;   // ~12px bold header font
const CELL_PADDING = 26;       // 6px side padding + border + breathing room

const clamp = (value) =>
    Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, value)));

// A column may render JSX (badges, action buttons). Those cannot be measured,
// so fall back to the raw field value when the column has one, and to the
// header label otherwise.
const getMeasurableText = (column, row) => {
    if (typeof column.render === "function") {
        let rendered;
        try {
            rendered = column.render(row, 0);
        } catch {
            rendered = null;
        }
        if (typeof rendered === "string" || typeof rendered === "number") {
            return String(rendered);
        }
    }
    return column.field ? String(row?.[column.field] ?? "") : "";
};

export const getDynamicColumnWidths = (columns = [], rows = []) => {
    const dataRows = Array.isArray(rows) ? rows : [];

    return columns.map((column) => {
        if (Array.isArray(column.children) && column.children.length > 0) {
            return { ...column, children: getDynamicColumnWidths(column.children, dataRows) };
        }

        const longestValue = dataRows.reduce((longest, row) => {
            const length = getMeasurableText(column, row).length;
            return length > longest ? length : longest;
        }, 0);

        const headerWidth = String(column.label ?? "").length * HEADER_CHAR_WIDTH;
        const bodyWidth = longestValue * BODY_CHAR_WIDTH;

        return {
            ...column,
            width: `${clamp(Math.max(headerWidth, bodyWidth) + CELL_PADDING)}px`,
        };
    });
};

export default getDynamicColumnWidths;
